import React, { Fragment, useMemo } from "react";
import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkGfm from "remark-gfm";
import twemoji from "twemoji";

const EMOJI_RE =
  /(\p{Regional_Indicator}{2}|[#*0-9]\uFE0F?\u20E3|\p{Extended_Pictographic}(?:\uFE0F|\uFE0E)?(?:\p{Emoji_Modifier})?(?:\u200D\p{Extended_Pictographic}(?:\uFE0F|\uFE0E)?(?:\p{Emoji_Modifier})?)*)/gu;

const splitTextWithEmojis = (text) => {
  const value = String(text || "");
  const parts = [];
  let lastIndex = 0;

  for (const match of value.matchAll(EMOJI_RE)) {
    const index = match.index ?? 0;
    const emoji = match[0] || "";
    if (index > lastIndex) {
      parts.push({ type: "text", value: value.slice(lastIndex, index) });
    }
    parts.push({ type: "emoji", value: emoji });
    lastIndex = index + emoji.length;
  }

  if (lastIndex < value.length) {
    parts.push({ type: "text", value: value.slice(lastIndex) });
  }

  return parts;
};

const emojiToTwemojiUrl = (emoji) => {
  const codePoint = twemoji?.convert?.toCodePoint
    ? twemoji.convert.toCodePoint(String(emoji || ""))
    : "";
  if (!codePoint) return "";

  const base =
    (process.env.REACT_APP_TWEMOJI_BASE_URL || "").trim() ||
    // cdnjs serves Twemoji assets with permissive CORS, which @react-pdf/renderer needs in the browser.
    "https://cdnjs.cloudflare.com/ajax/libs/twemoji/14.0.2/72x72";
  const normalizedBase = base.replace(/\/+$/, "");
  return `${normalizedBase}/${codePoint}.png`;
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 92,
    paddingBottom: 76,
    paddingHorizontal: 36,
    fontSize: 11,
    color: "#0f172a",
    fontFamily: "Helvetica",
    lineHeight: 1.35,
  },
  header: {
    position: "absolute",
    top: 24,
    left: 36,
    right: 36,
    height: 54,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#e2e8f0",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
  },
  footer: {
    position: "absolute",
    bottom: 24,
    left: 36,
    right: 36,
    height: 36,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: "#e2e8f0",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  brand: { flexDirection: "row", alignItems: "center", gap: 10 },
  // Keep the logo visibly large but never stretched.
  logo: { objectFit: "contain" },
  enterpriseName: { fontSize: 12, fontWeight: 700, color: "#0f172a" },
  subtitle: { fontSize: 9, color: "#64748b", marginTop: 2 },
  pageNumber: { fontSize: 9, color: "#64748b" },
  confidential: { fontSize: 9, color: "#64748b", textAlign: "center" },

  sectionTitle: {
    fontSize: 10,
    fontWeight: 700,
    color: "#334155",
    marginBottom: 8,
  },

  paragraph: { marginBottom: 8 },
  bold: { fontWeight: 700 },
  italic: { fontStyle: "italic" },
  codeInline: {
    fontFamily: "Courier",
    fontSize: 10,
    backgroundColor: "#f1f5f9",
  },
  codeBlock: {
    backgroundColor: "#0b1220",
    borderRadius: 8,
    padding: 10,
    marginBottom: 10,
  },
  codeBlockText: {
    fontFamily: "Courier",
    fontSize: 9.5,
    color: "#e2e8f0",
    lineHeight: 1.25,
  },
  h1: { fontSize: 16, fontWeight: 800, marginBottom: 8, marginTop: 10 },
  h2: { fontSize: 14, fontWeight: 800, marginBottom: 8, marginTop: 10 },
  h3: { fontSize: 12.5, fontWeight: 800, marginBottom: 6, marginTop: 8 },
  h4: { fontSize: 11.5, fontWeight: 800, marginBottom: 6, marginTop: 8 },
  hr: { height: 1, backgroundColor: "#e2e8f0", marginVertical: 12 },
  blockquote: {
    borderLeftWidth: 3,
    borderLeftColor: "#94a3b8",
    paddingLeft: 10,
    marginBottom: 10,
    color: "#334155",
  },
  listItem: { flexDirection: "row", gap: 8, marginBottom: 4 },
  listBullet: { width: 18, textAlign: "right", color: "#334155" },
  listContent: { flex: 1 },

  inlineFlow: { flexDirection: "row", flexWrap: "wrap" },
  inlineEmoji: { width: 12, height: 12, marginTop: 1 },

  table: {
    borderWidth: 1,
    borderColor: "#cbd5e1",
    borderRadius: 4,
    overflow: "hidden",
    marginBottom: 10,
  },
  tableRow: { flexDirection: "row" },
  tableCell: {
    flex: 1,
    borderRightWidth: 1,
    borderRightColor: "#cbd5e1",
    borderBottomWidth: 1,
    borderBottomColor: "#cbd5e1",
    paddingVertical: 4,
    paddingHorizontal: 6,
  },
  tableHeaderCell: { backgroundColor: "#f1f5f9" },
});

const nodeText = (node) => {
  if (!node) return "";
  if (node.type === "text") return node.value || "";
  if (Array.isArray(node.children)) return node.children.map(nodeText).join("");
  return "";
};

const PdfMarkdown = ({ markdown = "" }) => {
  const tree = useMemo(() => {
    try {
      return unified().use(remarkParse).use(remarkGfm).parse(markdown || "");
    } catch {
      return { type: "root", children: [{ type: "paragraph", children: [{ type: "text", value: String(markdown || "") }] }] };
    }
  }, [markdown]);

  const renderInlineSegments = (node, activeStyle = {}, keyPrefix = "i") => {
    if (!node) return null;

    switch (node.type) {
      case "text":
        return splitTextWithEmojis(node.value || "").map((p, i) =>
          p.type === "emoji" ? (
            <Image
              key={`${keyPrefix}-emj-${i}`}
              src={emojiToTwemojiUrl(p.value)}
              style={styles.inlineEmoji}
            />
          ) : (
            <Text
              key={`${keyPrefix}-txt-${i}`}
              style={activeStyle}
            >
              {p.value}
            </Text>
          )
        );
      case "strong":
        return (node.children || []).flatMap((c, i) =>
          renderInlineSegments(c, { ...activeStyle, ...styles.bold }, `${keyPrefix}-s-${i}`)
        );
      case "emphasis":
        return (node.children || []).flatMap((c, i) =>
          renderInlineSegments(c, { ...activeStyle, ...styles.italic }, `${keyPrefix}-e-${i}`)
        );
      case "inlineCode":
        return splitTextWithEmojis(node.value || "").map((p, i) =>
          p.type === "emoji" ? (
            <Image
              key={`${keyPrefix}-code-emj-${i}`}
              src={emojiToTwemojiUrl(p.value)}
              style={styles.inlineEmoji}
            />
          ) : (
            <Text
              key={`${keyPrefix}-code-txt-${i}`}
              style={{ ...activeStyle, ...styles.codeInline }}
            >
              {p.value}
            </Text>
          )
        );
      case "break":
        return [<Text key={`${keyPrefix}-br`}>{"\n"}</Text>];
      case "link": {
        const label = nodeText(node) || node.url || "";
        return splitTextWithEmojis(label).map((p, i) =>
          p.type === "emoji" ? (
            <Image
              key={`${keyPrefix}-link-emj-${i}`}
              src={emojiToTwemojiUrl(p.value)}
              style={styles.inlineEmoji}
            />
          ) : (
            <Text
              key={`${keyPrefix}-link-txt-${i}`}
              style={{ ...activeStyle, color: "#2563eb" }}
            >
              {p.value}
            </Text>
          )
        );
      }
      default:
        if (Array.isArray(node.children)) {
          return node.children.flatMap((c, i) =>
            renderInlineSegments(c, activeStyle, `${keyPrefix}-d-${i}`)
          );
        }
        return [];
    }
  };

  const renderTableCellContent = (cellNode, keyPrefix, activeStyle = {}) => {
    const children = Array.isArray(cellNode?.children) ? cellNode.children : [];

    if (children.length === 0) {
      return <Text key={`${keyPrefix}-empty`}> </Text>;
    }

    return children.map((child, idx) => {
      if (child?.type === "paragraph") {
        return (
          <View key={`${keyPrefix}-p-${idx}`} style={styles.inlineFlow}>
            {child.children?.flatMap((c, i) =>
              renderInlineSegments(
                c,
                activeStyle,
                `${keyPrefix}-p-${idx}-${i}`
              )
            )}
          </View>
        );
      }

      return (
        <View key={`${keyPrefix}-n-${idx}`} style={styles.inlineFlow}>
          {renderInlineSegments(child, activeStyle, `${keyPrefix}-n-${idx}`)}
        </View>
      );
    });
  };

  const renderBlock = (node, keyPrefix = "b", orderedIndex = 0) => {
    if (!node) return null;

    switch (node.type) {
      case "paragraph":
        return (
          <View key={`${keyPrefix}-p`} style={styles.paragraph}>
            <View style={styles.inlineFlow}>
              {node.children?.flatMap((c, i) =>
                renderInlineSegments(c, {}, `${keyPrefix}-p-${i}`)
              )}
            </View>
          </View>
        );
      case "heading": {
        const depth = Number(node.depth || 1);
        const style =
          depth === 1
            ? styles.h1
            : depth === 2
              ? styles.h2
              : depth === 3
                ? styles.h3
                : styles.h4;
        return (
          <View key={`${keyPrefix}-h`} style={style}>
            <View style={styles.inlineFlow}>
              {node.children?.flatMap((c, i) =>
                renderInlineSegments(c, {}, `${keyPrefix}-h-${i}`)
              )}
            </View>
          </View>
        );
      }
      case "thematicBreak":
        return <View key={`${keyPrefix}-hr`} style={styles.hr} />;
      case "blockquote":
        return (
          <View key={`${keyPrefix}-bq`} style={styles.blockquote}>
            {node.children?.map((c, i) => (
              <Fragment key={`${keyPrefix}-bq-${i}`}>{renderBlock(c, `${keyPrefix}-bq-${i}`)}</Fragment>
            ))}
          </View>
        );
      case "code":
        return (
          <View key={`${keyPrefix}-cb`} style={styles.codeBlock}>
            <Text style={styles.codeBlockText}>{node.value || ""}</Text>
          </View>
        );
      case "list": {
        const ordered = Boolean(node.ordered);
        return (
          <View key={`${keyPrefix}-list`} style={{ marginBottom: 10 }}>
            {(node.children || []).map((item, i) =>
              renderBlock(item, `${keyPrefix}-li-${i}`, ordered ? i + (node.start || 1) : 0)
            )}
          </View>
        );
      }
      case "listItem": {
        const bullet = orderedIndex ? `${orderedIndex}.` : "•";
        const children = (node.children || []).flatMap((c) => (c.type === "paragraph" ? c.children || [] : [c]));
        return (
          <View key={`${keyPrefix}-item`} style={styles.listItem}>
            <Text style={styles.listBullet}>{bullet}</Text>
            <View style={styles.listContent}>
              <View style={styles.inlineFlow}>
                {children.flatMap((c, i) =>
                  renderInlineSegments(c, {}, `${keyPrefix}-it-${i}`)
                )}
              </View>
            </View>
          </View>
        );
      }
      case "table": {
        const rows = Array.isArray(node.children) ? node.children : [];
        const colCount =
          rows.reduce(
            (max, row) =>
              Math.max(
                max,
                Array.isArray(row?.children) ? row.children.length : 0,
              ),
            0,
          ) || 1;

        return (
          <View key={`${keyPrefix}-tbl`} style={styles.table}>
            {rows.map((row, rowIndex) => {
              const cells = Array.isArray(row?.children) ? row.children : [];
              const isHeader = rowIndex === 0;
              const cellTextStyle = isHeader ? { fontWeight: 700 } : {};

              return (
                <View
                  key={`${keyPrefix}-tr-${rowIndex}`}
                  style={styles.tableRow}
                >
                  {Array.from({ length: colCount }).map((_, colIndex) => {
                    const cellNode = cells[colIndex] || null;
                    const isLastCol = colIndex === colCount - 1;
                    const isLastRow = rowIndex === rows.length - 1;

                    return (
                      <View
                        key={`${keyPrefix}-td-${rowIndex}-${colIndex}`}
                        style={[
                          styles.tableCell,
                          isHeader ? styles.tableHeaderCell : null,
                          isLastCol ? { borderRightWidth: 0 } : null,
                          isLastRow ? { borderBottomWidth: 0 } : null,
                        ]}
                      >
                        {cellNode
                          ? renderTableCellContent(
                              cellNode,
                              `${keyPrefix}-tdc-${rowIndex}-${colIndex}`,
                              cellTextStyle,
                            )
                          : null}
                      </View>
                    );
                  })}
                </View>
              );
            })}
          </View>
        );
      }
      default:
        if (Array.isArray(node.children)) {
          return node.children.map((c, i) => (
            <Fragment key={`${keyPrefix}-d-${i}`}>{renderBlock(c, `${keyPrefix}-d-${i}`)}</Fragment>
          ));
        }
        return null;
    }
  };

  return <View>{(tree.children || []).map((c, i) => renderBlock(c, `root-${i}`))}</View>;
};

const ChatMessageExportPDF = ({
  enterpriseName = "",
  logoDataUrl = "",
  logoWidth,
  logoHeight,
  title = "Chat Export",
  subtitle = "",
  markdown = "",
  exportedAtLabel = "",
}) => {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header} fixed>
          {logoDataUrl ? (
            <Image
              src={logoDataUrl}
              style={[
                styles.logo,
                {
                  width: Number(logoWidth) > 0 ? Number(logoWidth) : 180,
                  height: Number(logoHeight) > 0 ? Number(logoHeight) : 54,
                },
              ]}
            />
          ) : null}
        </View>

        <View style={styles.footer} fixed>
          <View style={{ width: "30%" }} />

          <View style={{ width: "40%", alignItems: "center" }}>
            <Text style={styles.confidential}>Confidential</Text>
          </View>

          <View style={{ width: "30%", alignItems: "flex-end" }}>
            <Text
              style={styles.pageNumber}
              fixed
              render={({ pageNumber }) => `Page - ${pageNumber}`}
            />
          </View>
        </View>

        {title ? <Text style={styles.sectionTitle}>{title}</Text> : null}
        <PdfMarkdown markdown={markdown} />
      </Page>
    </Document>
  );
};

export default ChatMessageExportPDF;
